import { get, map } from "lodash";
import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Visibility as ViewIcon } from "@mui/icons-material";

import PoonaWallaModal from "../PoonaWallaModal";
import PoonaWallaBasicLoader from "../PoonaWallaBasicLoader";
import PoonaWallaTable from "../PoonaWallaTable";
import PoonaWallaTooltip from "../PoonaWallaTooltip";

import { apiListingOpenApi } from "../../apis/portal";
import { showErrorToaster } from "../../globals/toaster";

import "./APIModal.scss";
import HeaderSearchInput from "../HeaderSearchInput";
import { POONAWALLA_APPLICATION_ENVIRONMENTS_TYPES } from "../../globals/constants";
import HeaderDropdown from "../HeaderDropdown";

const APIModal = () => {
  const location = useLocation();
  const history = useHistory();
  const qp = new URLSearchParams(location.search);

  const [categoryDetailsActive, setCategoryDetailsActive] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    filter: {
      ...(qp.get("apimodal") && { category_id: qp.get("apimodal") }),
    },
    page: 1,
    limit: 10,
    sort: {},
    attributes: ["id", "name", "description", "environment", "format", "scope"],
    detailed: false,
  });

  const [apiData, setApiData] = useState([]);
  const [apiCount, setApiCount] = useState(0);

  const handleGetApiListing = async () => {
    try {
      setIsLoading(true);
      const resp = await apiListingOpenApi(filters);
      const data = map(get(resp, "data.data", []), (api) => ({
        ...api,
        category: get(api, "category.name"),
      }));
      setApiData(data);
      setApiCount(get(resp, "data.count", 0));
    } catch (error) {
      showErrorToaster(get(error, "data.detail", "Something went wrong."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnmount = () => {
    qp.delete("apimodal");
    qp.delete("name");

    history.replace({
      search: qp.toString(),
    });
  };

  const handlePagination = (e, pg) => setFilters({ ...filters, page: pg });

  const textFormatter = (cell, row) => {
    return (
      <PoonaWallaTooltip title={cell}>
        <span>{cell}</span>
      </PoonaWallaTooltip>
    );
  };

  const environmentFormatter = (cell, row) => {
    return (
      <PoonaWallaTooltip title={cell}>
        <span className="text-center">{cell}</span>
      </PoonaWallaTooltip>
    );
  };

  const documentFormatter = (cell, row) => {
    return (
      <PoonaWallaTooltip title="View API Document">
        <Link to={`/apidocument/${cell}`}>
          <ViewIcon className="text-secondary" />
        </Link>
      </PoonaWallaTooltip>
    );
  };

  const handleFiltering = (type, value) => {
    setFilters({
      ...filters,
      filter: {
        ...filters.filter,
        [type]: value,
      },
    });
  };

  useEffect(() => {
    if (qp.get("apimodal") && qp.get("name")) {
      setFilters({
        ...filters,
        filter: {
          ...(qp.get("apimodal") && { category_id: qp.get("apimodal") }),
        },
      });
      setCategoryDetailsActive({
        id: qp.get("apimodal"),
        name: qp.get("name"),
      });
    } else {
      setCategoryDetailsActive(undefined);
    }
  }, [location.search]);

  useEffect(() => {
    if (!get(categoryDetailsActive, "id")) return;

    handleGetApiListing();
  }, [categoryDetailsActive, filters]);

  return (
    <div className="APIModal">
      <PoonaWallaModal
        size="xl"
        handleHideModal={handleUnmount}
        isActive={categoryDetailsActive}
        title={get(categoryDetailsActive, "name", "APIs")}
      >
        <div>
          <PoonaWallaTable
            page={filters.page}
            limit={filters.limit}
            count={apiCount}
            columns={[
              {
                dataField: "name",
                text: "API Name",
                sort: false,
                formatter: textFormatter,
                headerFormatter: () => (
                  <HeaderSearchInput
                    placeholder="API Name"
                    handleChange={(val) => handleFiltering("name", val)}
                  />
                ),
                headerStyle: () => ({
                  width: "20rem",
                  fontSize: "14px",
                  verticalAlign: "middle",
                }),
                style: () => ({
                  fontSize: "14px",
                }),
                classes: "font-weight-bold",
              },
              {
                dataField: "description",
                text: "Description",
                sort: false,
                formatter: textFormatter,
                headerFormatter: () => (
                  <HeaderSearchInput
                    placeholder="Description"
                    handleChange={(val) => handleFiltering("description", val)}
                  />
                ),
                headerStyle: () => ({
                  width: "20rem",
                  fontSize: "14px",
                  verticalAlign: "middle",
                }),
                style: () => ({
                  fontSize: "14px",
                }),
                classes: "font-weight-bold",
              },
              {
                dataField: "environment",
                text: "Environment",
                sort: false,
                formatter: environmentFormatter,
                headerStyle: () => ({
                  width: "15rem",
                  fontSize: "14px",
                  verticalAlign: "middle",
                }),
                headerFormatter: () => (
                  <HeaderDropdown
                    placeholder="Environment"
                    options={POONAWALLA_APPLICATION_ENVIRONMENTS_TYPES}
                    handleChange={(val) => handleFiltering("environment", val)}
                  />
                ),
                style: () => ({
                  fontSize: "14px",
                }),
                classes: "font-weight-bold",
              },
              {
                dataField: "id",
                text: "Document",
                sort: false,
                formatter: documentFormatter,
                headerStyle: () => ({
                  width: "10rem",
                  textAlign: "center",
                  verticalAlign: "middle",
                }),
                classes: "text-center",
              },
            ]}
            classes="poonaWalla-table-primary"
            wrapperClasses="table-responsive"
            data={apiData}
            keyField="id"
            sortingConfig={{}}
            pagination={true}
            handlePagination={handlePagination}
            isLoading={isLoading}
          />
        </div>
      </PoonaWallaModal>
    </div>
  );
};

export default APIModal;
